import React, { useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import OutlinedInput from '@mui/material/OutlinedInput';
import Select from '@mui/material/Select';
import IconButton from '@mui/material/IconButton';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import InputBox from '../../../atomic/inputbox/InputBox';
import { getStatusMenuProps, MenuProps, sharedInputSx } from '../../Products/kitchenProductsConstants';
import { STATUS_TABS } from '../orderManagementConfig';
import { OrderTrackingDatePicker } from './OrderTrackingDatePicker';
import { OrderTrackingActiveFilters } from './OrderTrackingActiveFilters';
import { getActiveFilterChips } from './orderTrackingFilterSummary';
import { MAP_MARKER_VIEW_TABS } from './constants';

function statusOptionLabel(opt) {
  return opt.value === 'all' ? 'All orders' : opt.label;
}

function StatusMenuItem({ opt, count }) {
  return (
    <Box
      component='span'
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
        gap: 1,
      }}
    >
      <span>{statusOptionLabel(opt)}</span>
      <Box component='span' sx={{ fontWeight: 700, fontSize: 12, color: '#3F3F6E', flexShrink: 0 }}>
        {count}
      </Box>
    </Box>
  );
}

const DELIVERY_TYPE_OPTIONS = [
  { value: 'all', label: 'All types' },
  { value: 'home delivery', label: 'Home delivery' },
  { value: 'pickup', label: 'Pickup' },
];

const selectSx = {
  minWidth: 160,
  ...sharedInputSx,
  '& .MuiSelect-select': {
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
  },
};

export function OrderTrackingMapToolbar({
  loading,
  onRefresh,
  deliveryDate,
  onDeliveryDateChange,
  statusFilter,
  onStatusFilterChange,
  statusTabCounts,
  deliveryTypeFilter,
  onDeliveryTypeFilterChange,
  orderIdSearch,
  onOrderIdSearchChange,
  filterState,
  lastRefreshLabel,
  mapMarkerView,
  onMapMarkerViewChange,
}) {
  const hasFilterChips = useMemo(
    () =>
      getActiveFilterChips(filterState).filter((chip) => chip.key !== 'source').length > 0,
    [filterState]
  );

  return (
    <header className='otd-map-toolbar'>
      <div className='otd-map-toolbar__row'>
        <IconButton
          component={RouterLink}
          to='/ordermanagement'
          aria-label='Back to orders'
          size='small'
          className='otd-map-toolbar__back'
        >
          <ArrowBackIcon fontSize='small' />
        </IconButton>

        <div className='otd-map-toolbar__control-bar'>
          <div className='otd-map-toolbar__top-row'>
            <Box sx={{ width: { xs: '100%', md: 180 }, flexShrink: 0 }}>
              <OrderTrackingDatePicker
                deliveryDate={deliveryDate}
                onDeliveryDateChange={onDeliveryDateChange}
                tone='light'
              />
            </Box>

            <FormControl
              size='small'
              sx={{
                minWidth: 200,
                maxWidth: 220,
                ...selectSx,
              }}
            >
              <InputLabel id='otd-map-status-label'>Status</InputLabel>
              <Select
                labelId='otd-map-status-label'
                id='otd-map-status'
                value={statusFilter}
                label='Status'
                onChange={(e) => onStatusFilterChange(e.target.value)}
                MenuProps={getStatusMenuProps(false)}
                renderValue={(value) => {
                  const opt = STATUS_TABS.find((t) => t.value === value) ?? STATUS_TABS[0];
                  const count = statusTabCounts?.[opt.value] ?? 0;
                  return `${statusOptionLabel(opt)} (${count})`;
                }}
              >
                {STATUS_TABS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    <StatusMenuItem opt={opt} count={statusTabCounts?.[opt.value] ?? 0} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size='small' sx={selectSx}>
              <InputLabel id='otd-map-delivery-type-label'>Delivery type</InputLabel>
              <Select
                labelId='otd-map-delivery-type-label'
                id='otd-map-delivery-type'
                value={deliveryTypeFilter}
                label='Delivery type'
                onChange={(e) => onDeliveryTypeFilterChange(e.target.value)}
                input={<OutlinedInput label='Delivery type' />}
                MenuProps={MenuProps}
              >
                {DELIVERY_TYPE_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <InputBox
              type='text'
              label='Search orders'
              size='small'
              height='auto'
              name='orderIdSearch'
              value={orderIdSearch}
              onChange={(e) => onOrderIdSearchChange(e.target.value)}
              sx={{
                width: { xs: '100%', md: 260 },
                minWidth: 180,
                flex: '1 1 200px',
                ...sharedInputSx,
              }}
            />

            <div className='otd-map-toolbar__view-tabs' role='tablist' aria-label='Map markers'>
              {MAP_MARKER_VIEW_TABS.map((tab) => {
                const active = mapMarkerView === tab.id;
                return (
                  <button
                    key={tab.id}
                    type='button'
                    role='tab'
                    aria-selected={active}
                    className={`otd-map-toolbar__view-tab ${active ? 'otd-map-toolbar__view-tab--active' : ''}`}
                    onClick={() => onMapMarkerViewChange(tab.id)}
                  >
                    {tab.label}
                  </button>
                );
              })}
            </div>

            <div className='otd-map-toolbar__last-refresh' aria-live='polite'>
              <span>
                Last updated{' '}
                <span className='otd-map-toolbar__last-refresh-time'>
                  {loading ? 'Refreshing…' : lastRefreshLabel || '—'}
                </span>
              </span>
              <span className='otd-map-toolbar__last-refresh-sep'>·</span>
              <button
                type='button'
                className='otd-map-toolbar__refresh-link'
                onClick={onRefresh}
                disabled={loading}
              >
                Refresh
              </button>
            </div>
          </div>
        </div>
      </div>

      {hasFilterChips ? (
        <OrderTrackingActiveFilters
          filterState={filterState}
          lastRefreshLabel={lastRefreshLabel}
          loading={loading}
          variant='toolbar'
        />
      ) : null}
    </header>
  );
}
